"use client";

import { useCallback } from "react";
import { mixpanel, isMixpanelInitialized } from "./mixpanel";
import { useLanguage } from "./language-context";
import { useSettings } from "./settings-context";

// Event names as constants for type safety
export const AnalyticsEvents = {
  // Tier 1: Critical
  APP_OPENED: "app_opened",
  ONBOARDING_COMPLETED: "onboarding_completed",
  LANGUAGE_CHANGED: "language_changed",
  TOPIC_SELECTED: "topic_selected",
  DIALOGUE_STARTED: "dialogue_started",
  DIALOGUE_COMPLETED: "dialogue_completed",
  CUSTOM_TOPIC_CREATED: "custom_topic_created",

  // Tier 2: Engagement
  VOICE_RESPONSE_GIVEN: "voice_response_given",
  DIALOGUE_FEEDBACK: "dialogue_feedback",
  VOCABULARY_SEARCHED: "vocabulary_searched",
  WORD_MASTERED: "word_mastered",
  PHRASE_REVEALED: "phrase_revealed",
  TTS_PLAYED: "tts_played",
  HELP_REQUESTED: "help_requested",

  // Tier 3: Technical
  API_ERROR: "api_error",
  SPEECH_RECOGNITION_FAILED: "speech_recognition_failed",
  GENERATION_FAILED: "generation_failed",
} as const;


/**
 * Hook that provides analytics tracking with app context automatically included.
 * Wraps Mixpanel with target_language and native_language included in all events.
 */
export function useAnalytics() {
  const { targetLanguage, nativeLanguage } = useLanguage();
  const { userProfile, theme } = useSettings();

  /**
   * Track an event with optional properties.
   * Automatically includes target_language and native_language.
   */
  const track = useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      if (!isMixpanelInitialized()) {
        return;
      }

      mixpanel.track(event, {
        target_language: targetLanguage.code,
        native_language: nativeLanguage.code,
        ...properties,
      });
    },
    [targetLanguage.code, nativeLanguage.code]
  );

  /**
   * Identify a user by their unique ID.
   * Call this on first visit with a generated UUID.
   */
  const identify = useCallback((userId: string) => {
    if (!isMixpanelInitialized()) {
      return;
    }

    mixpanel.identify(userId);
  }, []);

  /**
   * Set user profile properties (super properties).
   * These are sent with every subsequent event.
   */
  const setUserProperties = useCallback(
    (props: Record<string, unknown>) => {
      if (!isMixpanelInitialized()) {
        return;
      }

      mixpanel.people.set(props);
    },
    []
  );

  /**
   * Register super properties that are sent with every event.
   */
  const registerSuperProperties = useCallback(() => {
    if (!isMixpanelInitialized()) {
      return;
    }

    mixpanel.register({
      target_language: targetLanguage.code,
      native_language: nativeLanguage.code,
      speaking_style: userProfile.speakingStyle || "unknown",
      theme: theme,
      onboarding_completed: Boolean(userProfile.name),
    });
  }, [targetLanguage.code, nativeLanguage.code, userProfile.speakingStyle, userProfile.name, theme]);

  /**
   * Reset the current user (call on logout or data clear).
   */
  const reset = useCallback(() => {
    if (!isMixpanelInitialized()) {
      return;
    }

    mixpanel.reset();
  }, []);

  return {
    track,
    identify,
    setUserProperties,
    registerSuperProperties,
    reset,
    events: AnalyticsEvents,
  };
}
